import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import Proposal from "work/proposals/proposal";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";

const ListContainer = styled(Grid)(() => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  width: "100%",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  // No left margin: the table meets the divider between the two columns. `HeadControls` above it
  // already carries the prose inset — see `workPaneProseInset`.
  overflowY: "auto",
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type PoliciesTabProps = {
  proposal: Proposal;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const { proposal } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const policyDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download the document. Please try again later.", {
        variant: "error",
      });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  const selectedDocuments = session.context?.viewingAsVendor ? proposal.vendorPolicies : proposal.clientPolicies;

  if (Masked.isMasked(selectedDocuments)) {
    return maskedProposalFieldNotice(MaskedProposalField.Policies);
  }
  return (
    <>
      <HeadControls>
        {selectedDocuments.length === 0 && <HelperText>No policy documents</HelperText>}
      </HeadControls>
      <ListContainer container direction="row">
        <Grid>
          {selectedDocuments.length > 0 && (
            <DocumentList
              documents={selectedDocuments}
              documentType={
                session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
              }
              downloadingFileId={isDownloadingFile}
              onDownload={handleDownloadDocumentById}
            />
          )}
        </Grid>
      </ListContainer>
    </>
  );
}
